import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import anime from "animejs/lib/anime.es.js";
import { PixiSettings } from "../../../../pixiSettings";
import { formatToCustomLocale } from "../../../../utilities";
import { getUITexture } from "../../../../assets";

// Device-specific scale multipliers for the popup
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { scale: 2.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { scale: 1.5 },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { scale: 2.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { scale: 1.8 },
  },
  [PixiSettings.DEVICE.DESKTOP]: { scale: 1.2 },
};

// Text slot names from the spine (bonus_buy_popup.json)
const SLOTS = {
  TEXT: "txt_slot_popup_txt", // title + description stacked
  VALUE: "txt_slot_popup_value", // cost value on the button
};

/**
 * BuyBonusPopupController — spine-based buy bonus popup.
 *
 * Lifecycle:
 * 1. constructor → initializeSpine (create, addChild, alpha 0.001, warmUp, initializeTexts)
 * 2. show(bonusCost) → overlay fadeIn + set animation at alpha 0.001  → rAF reveal (alpha=1)
 * 3. hide(cb) → play "out" → overlay fade out → cb
 * 4. cleanup()
 *
 * KEY FIX: show() sets animation while still at alpha 0.001, then reveals on next frame
 * via requestAnimationFrame — same pattern as FreeSpinSceneTransitionController.playAnimation()
 * This prevents first-frame jitter caused by GPU compiling shaders for the new pose.
 */
class BuyBonusPopupController {
  constructor(container, dimensionsContext, dispatch, overlayRef, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.overlayRef = overlayRef;
    this.t = t;
    this.dimensions = dimensionsContext.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.currentTimeout = null;
    this._showRAF = null;

    // Text elements — created once in initializeTexts(), reused across show/hide cycles
    this.textContainer = null; // title + description in txt_slot_popup_txt
    this.valueContainer = null; // cost value in txt_slot_popup_value
    this.titleText = null;
    this.descText = null;
    this.valueText = null;

    // Close button (child of spine — scales automatically)
    this.closeButton = null;
    this._closeHandler = null;

    this._availableAnimationsCache = null;

    this.initializeSpine();
  }

  // =========================================================================
  // SCALING & DIMENSIONS
  // =========================================================================

  getDeviceConfig() {
    if (!this.dimensionsContext) return { scale: 1 };
    const { device, orientation } = this.dimensionsContext;
    const cfg = DEVICE_CONFIG[device];
    return cfg?.[orientation] ?? cfg ?? { scale: 1 };
  }

  applyScaling() {
    if (!this.spine?.scale || !this.spine.parent) return;

    let scale = this.dimensionsContext?.uiSymbolScale || 1;
    scale = Math.max(0.1, Math.min(scale, 2));

    const deviceConfig = this.getDeviceConfig();
    scale *= deviceConfig.scale || 1;

    try {
      this.spine.scale.set(scale);
    } catch {
      // Silent fail
    }
  }

  updateDimensions(dimensionsContext) {
    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensionsContext?.dimensions?.width ||
      this.dimensions.height !== dimensionsContext?.dimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;
    if (!dimensionsChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;

    if (this.spine && this.initialized) {
      this.applyScaling();
    }
  }

  // =========================================================================
  // SPINE INITIALIZATION
  // =========================================================================

  initializeSpine() {
    try {
      this.spine = this.createSpine();
      if (!this.spine) return;

      if (
        !this.spine.skeleton ||
        !this.spine.state ||
        !this.spine.skeleton.data
      )
        return;

      this.container.addChild(this.spine);
      this.spine.alpha = 0.001;
      this.spine.zIndex = 20;

      this.applyScaling();

      if (this.spine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      this.warmUp();

      // Create all text elements at init time (IntroPopupController pattern)
      this.initializeTexts();

      // Close button — added as child of spine so it scales with it
      this.initializeCloseButton();
    } catch (error) {
      // Silent fail
    }
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has("bonus_buy_popup-skeleton") ||
        !assets.has("bonus_buy_popup-atlas")
      )
        return null;

      const spine = Spine.from({
        skeleton: "bonus_buy_popup-skeleton",
        atlas: "bonus_buy_popup-atlas",
      });

      if (!spine?.skeleton?.data?.animations || !spine?.state) return null;
      return spine;
    } catch (error) {
      return null;
    }
  }

  warmUp() {
    if (!this.spine?.state) return;
    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.spine.state.setAnimation(0, animations[0], false);
        this.spine.state.update(0.016);
        this.spine.skeleton.updateWorldTransform();
      }
      this.spine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;
    const animations = this.spine.skeleton.data.animations.map((a) => a.name);
    this._availableAnimationsCache = animations;
    return animations;
  }

  getSpineAnimationDuration(animationName) {
    if (!this.spine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.spine.skeleton.data.animations.find(
        (a) => a.name === animationName,
      );
      if (animation?.duration) return Math.round(animation.duration * 1000);
      return 1000;
    } catch {
      return 1000;
    }
  }

  // =========================================================================
  // TEXT INITIALIZATION — all created at mount time
  // =========================================================================

  initializeTexts() {
    if (!this.spine?.skeleton) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;

    // -----------------------------------------------------------------------
    // txt_slot_popup_txt — title + description stacked vertically
    // -----------------------------------------------------------------------
    const textSlot = this.spine.skeleton.findSlot(SLOTS.TEXT);
    if (textSlot) {
      this.textContainer = new PIXI.Container();
      this.textContainer.name = "buy_bonus_text_slot";
      this.textContainer.zIndex = 1000;

      // --- Title (two-layer, same style as BuyBonusController) ---
      const titleString = this.t("buy_bonus_title", { ns: "common" });
      const titleFontSize = 38;

      const sharedTitleStyle = {
        fontFamily: primaryStyle.fontFamily,
        fontSize: titleFontSize,
        fontWeight: "800",
        align: "center",
        letterSpacing: 1,
        padding: 10,
        lineHeight: titleFontSize * 1.1,
      };

      // Shadow layer
      this.titleShadow = new PIXI.Text(
        titleString,
        new PIXI.TextStyle({ ...sharedTitleStyle, ...primaryStyle.shadow }),
      );
      this.titleShadow.resolution = 2;
      this.titleShadow.anchor.set(0.5);
      this.titleShadow.y = -23 + (primaryStyle.shadowOffsetY || 3);

      // Top layer
      this.titleText = new PIXI.Text(
        titleString,
        new PIXI.TextStyle({ ...sharedTitleStyle, ...primaryStyle.top }),
      );
      this.titleText.resolution = 2;
      this.titleText.anchor.set(0.5);
      this.titleText.y = -23;

      // --- Description (single layer, white with stroke) ---
      const descString =
        this.t("buy_bonus_description", { ns: "common" }) || "";
      const descStyle = new PIXI.TextStyle({
        fontFamily: primaryStyle.fontFamily,
        fontSize: 16,
        fontWeight: "700",
        fill: "#ffffff",
        stroke: "#3a1878",
        strokeThickness: 3,
        lineJoin: "round",
        align: "center",
        letterSpacing: 0.5,
        padding: 10,
        wordWrap: true,
        wordWrapWidth: 280,
      });

      this.descText = new PIXI.Text(descString, descStyle);
      this.descText.resolution = 2;
      this.descText.anchor.set(0.5, 0);
      // Position description just below the centered title
      this.descText.y = titleFontSize - 5;

      this.textContainer.addChild(this.titleShadow);
      this.textContainer.addChild(this.titleText);
      this.textContainer.addChild(this.descText);

      this.spine.addSlotObject(SLOTS.TEXT, this.textContainer);
    }

    // -----------------------------------------------------------------------
    // txt_slot_popup_value — cost on the button (two-layer value + currency)
    // Value: large yellow text | Currency: 0.7× size, white, bottom-aligned
    // (same pattern as WinDisplay)
    // -----------------------------------------------------------------------
    const valueSlot = this.spine.skeleton.findSlot(SLOTS.VALUE);
    if (valueSlot) {
      this.valueContainer = new PIXI.Container();
      this.valueContainer.name = "buy_bonus_value_slot";
      this.valueContainer.zIndex = 1000;

      this.valueFontSize = 32;
      const sharedValueStyle = {
        fontFamily: primaryStyle.fontFamily,
        fontSize: this.valueFontSize,
        fontWeight: "800",
        align: "center",
        letterSpacing: 1,
        padding: 10,
      };

      // --- Value text (two-layer: shadow + yellow top) ---
      this.valueStyle = new PIXI.TextStyle({
        ...sharedValueStyle,
        ...primaryStyle.top,
        fill: "#ffbf11",
      });
      this.valueShadowStyle = new PIXI.TextStyle({
        ...sharedValueStyle,
        ...primaryStyle.shadow,
      });

      this.valueShadow = new PIXI.Text("", this.valueShadowStyle);
      this.valueShadow.resolution = 2;
      this.valueShadow.anchor.set(0, 0.5);
      this.valueShadow.y = primaryStyle.shadowOffsetY || 3;

      this.valueText = new PIXI.Text("", this.valueStyle);
      this.valueText.resolution = 2;
      this.valueText.anchor.set(0, 0.5);

      // --- Currency text (two-layer: shadow + white top, 0.7× font size) ---
      const currencyFontSize = Math.floor(this.valueFontSize * 0.7);
      const sharedCurrencyStyle = {
        fontFamily: primaryStyle.fontFamily,
        fontSize: currencyFontSize,
        fontWeight: "800",
        align: "center",
        letterSpacing: 1,
        padding: 10,
      };

      this.currencyStyle = new PIXI.TextStyle({
        ...sharedCurrencyStyle,
        ...primaryStyle.top,
        fill: "#ffffff",
      });
      this.currencyShadowStyle = new PIXI.TextStyle({
        ...sharedCurrencyStyle,
        ...primaryStyle.shadow,
      });

      this.currencyShadow = new PIXI.Text("", this.currencyShadowStyle);
      this.currencyShadow.resolution = 2;
      this.currencyShadow.anchor.set(0, 0.5);
      this.currencyShadow.y = primaryStyle.shadowOffsetY || 3;

      this.currencyText = new PIXI.Text("", this.currencyStyle);
      this.currencyText.resolution = 2;
      this.currencyText.anchor.set(0, 0.5);

      // Inner wrapper — Spine controls valueContainer's transform via the slot bone,
      // so we scale this inner container instead (same technique as slot text scaling)
      this.valueInner = new PIXI.Container();
      this.valueInner.name = "buy_bonus_value_inner";
      this.valueInner.addChild(this.valueShadow);
      this.valueInner.addChild(this.valueText);
      this.valueInner.addChild(this.currencyShadow);
      this.valueInner.addChild(this.currencyText);

      this.valueContainer.addChild(this.valueInner);
      this.spine.addSlotObject(SLOTS.VALUE, this.valueContainer);
    }
  }

  // =========================================================================
  // TEXT UPDATES
  // =========================================================================

  updateCost(bonusCost, currency) {
    if (!this.valueText || !this.valueShadow) return;

    const formatted = formatToCustomLocale(bonusCost);
    const currencyStr = currency ? ` ${currency}` : "";

    // Update text content
    this.valueText.text = formatted;
    this.valueShadow.text = formatted;

    if (this.currencyText && this.currencyShadow) {
      this.currencyText.text = currencyStr;
      this.currencyShadow.text = currencyStr;
    }

    // Measure widths to position elements side-by-side, centered
    const valueMetrics = PIXI.TextMetrics.measureText(
      formatted,
      this.valueStyle,
    );
    const currencyMetrics = currencyStr
      ? PIXI.TextMetrics.measureText(currencyStr, this.currencyStyle)
      : { width: 0, height: 0 };
    const totalWidth = valueMetrics.width + currencyMetrics.width;

    // Center the combined text block
    const startX = -totalWidth / 2;
    this.valueText.x = startX;
    this.valueShadow.x = startX;

    if (this.currencyText && this.currencyShadow) {
      const currencyX = startX + valueMetrics.width;
      this.currencyText.x = currencyX;
      this.currencyShadow.x = currencyX;

      // Bottom-align currency with value (same 0.3 factor as WinDisplay)
      const yOffset = Math.round(
        (valueMetrics.height - currencyMetrics.height) * 0.3,
      );
      this.currencyText.y = yOffset;
      this.currencyShadow.y = yOffset + (this.valueShadow.y || 0);
    }

    // Auto-scale: shrink inner wrapper if text overflows the button area
    // (Spine controls valueContainer's transform, so we scale valueInner instead)
    if (this.valueInner) {
      const maxTextWidth = 160;
      if (totalWidth > maxTextWidth && maxTextWidth > 0) {
        const fitScale = maxTextWidth / totalWidth;
        this.valueInner.scale.set(fitScale);
      } else {
        this.valueInner.scale.set(1);
      }
    }
  }

  // =========================================================================
  // SHOW / HIDE
  // =========================================================================

  /**
   * Show the popup.
   * KEY: Set animation while still at alpha 0.001, reveal on NEXT FRAME via rAF.
   * (same pattern as FreeSpinSceneTransitionController.playAnimation)
   */
  show(bonusCost, currency, onIntroComplete = null) {
    if (this.isPlaying || !this.spine?.state || !this.initialized) return;

    this.isPlaying = true;
    this.overlayRef.current?.fadeInBackground();

    if (this.spine && this.initialized) {
      this.applyScaling();
    }

    // Set animation while STILL HIDDEN (alpha 0.001) — lets GPU process new pose
    this.spine.state.clearTracks();
    this.spine.state.clearListeners();
    this.spine.state.apply(this.spine.skeleton);
    this.spine.skeleton.setToSetupPose();

    // Update cost text
    this.updateCost(bonusCost, currency);

    const hasIn = this.spine.skeleton.data.animations.find(
      (a) => a.name === "in",
    );
    if (hasIn) {
      this.spine.state.setAnimation(0, "in", false);

      const inDuration = this.getSpineAnimationDuration("in");
      this.currentTimeout = setTimeout(() => {
        if (this.isPlaying && this.spine?.state) {
          this.spine.state.setAnimation(0, "idle", true);
        }
        if (onIntroComplete) onIntroComplete();
      }, inDuration);
    } else {
      this.spine.state.setAnimation(0, "idle", true);
      if (onIntroComplete) onIntroComplete();
    }

    // Show on NEXT FRAME — old warmup pose is gone, new animation starts fresh
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) {
        this.spine.alpha = 1;
      }
    });
  }

  hide(onComplete) {
    if (!this.isPlaying || !this.spine?.state) {
      if (onComplete) onComplete();
      return;
    }

    // Cancel pending in→idle timeout — prevents it from overwriting the "out" animation
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }

    this.spine.state.clearListeners();

    const hasOut = this.spine.skeleton.data.animations.find(
      (a) => a.name === "out",
    );
    if (hasOut) {
      this.spine.state.setAnimation(0, "out", false);
      this.spine.state.addListener({
        complete: (entry) => {
          if (entry.animation?.name === "out" && this.isPlaying) {
            this.finishAnimation(onComplete);
          }
        },
      });
    } else {
      this.finishAnimation(onComplete);
    }
  }

  finishAnimation(onComplete) {
    if (!this.spine || !this.initialized) return;

    this.isPlaying = false;
    this.spine.alpha = 0.001;

    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }

    this.overlayRef.current?.fadeOutBackground(() => {
      if (onComplete) onComplete();
    });
  }

  // =========================================================================
  // BUTTON INTERACTIVITY
  // =========================================================================

  setButtonInteractive(onClick) {
    if (!this.valueContainer) return;
    this._buyClickHandler = onClick;
    this.valueContainer.eventMode = "static";
    this.valueContainer.cursor = "pointer";
    // Hit area matching the button artwork (253×100) from the spine pop_button slot
    this.valueContainer.hitArea = new PIXI.Rectangle(-130, -50, 260, 100);
    this.valueContainer.removeAllListeners?.();
    this.valueContainer.on("pointertap", onClick);
  }

  /**
   * Toggle the buy button's disabled visual state.
   * When disabled: text becomes semi-transparent, pointer events are blocked.
   * When enabled: text is fully opaque, pointer events are restored.
   */
  setButtonDisabledVisual(disabled) {
    if (!this.valueContainer) return;

    // Semi-transparent text for "can't afford" state
    if (this.valueInner) {
      this.valueInner.alpha = disabled ? 0.5 : 1;
    }

    // Show "not-allowed" cursor on hover when disabled; keep eventMode "static"
    // so the cursor is visible (click is guarded in BuyBonusPopup.jsx)
    this.valueContainer.cursor = disabled ? "not-allowed" : "pointer";
  }

  // =========================================================================
  // CLOSE BUTTON
  // =========================================================================

  initializeCloseButton() {
    const texture = getUITexture("icon_close_menu");
    if (!texture || !this.spine?.skeleton?.data) return;

    const sd = this.spine.skeleton.data;

    this.closeButton = new PIXI.Sprite(texture);
    this.closeButton.anchor.set(0.5);
    // Fixed size in spine-local coords — scales automatically with spine
    this.closeButton.width = 38;
    this.closeButton.height = 38;
    this.closeButton.eventMode = "static";
    this.closeButton.cursor = "pointer";
    this.closeButton.zIndex = 100;

    // Position at native spine top-right corner (spine-local space)
    this.closeButton.x = sd.width / 2 - 42;
    this.closeButton.y = -(sd.height / 2) + 20;

    this.closeButton.on("pointertap", () => {
      if (!this._closeHandler) return;
      // Press animation
      const ox = this.closeButton.scale.x;
      const oy = this.closeButton.scale.y;
      anime.remove(this.closeButton.scale);
      anime({
        targets: this.closeButton.scale,
        x: ox * 0.85,
        y: oy * 0.85,
        duration: 80,
        easing: "easeInQuad",
        complete: () => {
          anime({
            targets: this.closeButton.scale,
            x: ox,
            y: oy,
            duration: 180,
            easing: "easeOutQuad",
          });
        },
      });
      this._closeHandler();
    });

    this.spine.addChild(this.closeButton);
  }

  setCloseButtonHandler(handler) {
    this._closeHandler = handler;
  }

  // =========================================================================
  // CLEANUP
  // =========================================================================

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }

    // Cleanup text containers
    if (this.textContainer) {
      this.textContainer.destroy({ children: true });
      this.textContainer = null;
      this.titleText = null;
      this.titleShadow = null;
      this.descText = null;
    }
    if (this.valueContainer) {
      this.valueContainer.destroy({ children: true });
      this.valueContainer = null;
      this.valueText = null;
      this.valueShadow = null;
      this.currencyText = null;
      this.currencyShadow = null;
    }

    // Close button is a child of spine — destroyed with it
    this.closeButton = null;
    this._closeHandler = null;

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }

    this.initialized = false;
  }
}

export default BuyBonusPopupController;
